import { Linking, View } from "react-native";
import { RootTabScreenProps } from "../types";
import { useSelectedTeams } from "./ScheduleScreen";
import NavPage from "../components/pages/NavPage";
import ErrorBoundary from "../components/ErrorBoundary";
import { TeamsSelector } from "../components/TeamsSelector";
import DayPicker from "../components/DayPicker";
import React, { useContext, useEffect, useState } from "react";
import dayjs from "dayjs";
import { LanguageContext } from "../common/language";
import FixtureLabel from "../common/FixtureLabel";
import { useStoredPref } from "../data/useStoredPref";
import { canonicalDate, canonicalDateFormat } from "../common/scene";
import { DocumentSnapshot } from "@atgof-firebase/types";
import { useProject } from "../data/useProject";
import { useDocument } from "../data/useDocument";
import { Button, IconButton, ProgressBar } from "react-native-paper";
import { UserContext } from "../data/userContext";
import { FieldValue } from "@atgof-firebase/firebase";
import { ProjectContext } from "../data/projectContext";
import { FileUploads } from "../components/FileUploads";
import { getStorageURL } from "../config/firebase";

function OpenButton({ doc }: { doc: DocumentSnapshot }) {
  const { ph } = useContext(LanguageContext);
  const path: string | undefined = doc.get("path");
  const [url, setUrl] = useState<string>();
  useEffect(() => {
    if (!path) {
      setUrl(undefined);
      return;
    }
    let cancelled = false;
    getStorageURL(path).then((storageURL) => {
      if (!cancelled) setUrl(storageURL);
    });
    return () => {
      cancelled = true;
    };
  }, [path]);
  if (!url) return null;
  return (
    <Button onPress={() => Linking.openURL(url)}>{ph("open") as string}</Button>
  );
}

function CallSheet({
  team,
  day,
}: {
  team: DocumentSnapshot;
  day: dayjs.Dayjs;
}) {
  const { user } = React.useContext(UserContext);
  const { categories } = React.useContext(ProjectContext);
  const project = useProject();
  const callSheetRef = project
    ?.collection("days")
    .doc(day.format(canonicalDateFormat))
    .collection("callSheets")
    .doc(team.id);
  const callSheet = useDocument(callSheetRef);
  if (!categories.includes("admin")) return null; // TODO Read-only call sheet here
  if (!callSheet) return <ProgressBar indeterminate />;
  if (!callSheet.exists) {
    return (
      <IconButton
        icon="plus"
        onPress={() => {
          callSheetRef?.set({
            deleted: false,
            createdBy: user.ref,
            createdAt: FieldValue.serverTimestamp(),
            lastModifiedBy: user.ref,
            lastModifiedAt: FieldValue.serverTimestamp(),
          });
        }}
      />
    );
  }
  return (
    <FileUploads
      collectionRef={callSheetRef?.collection("attachments")}
      accept={["pdf"]}
      completedUploadElement={(doc) => <OpenButton doc={doc} />}
    />
  );
}

export default function CallSheetsScreen({}: RootTabScreenProps<"callSheets">) {
  const { ph } = React.useContext(LanguageContext);
  const [dayString, setDayString] = useStoredPref<string>(
    "CallSheetsScreen.day",
    "",
  );
  const day = React.useMemo(
    () => (dayString?.length ? dayjs(dayString, canonicalDateFormat) : dayjs()),
    [dayString],
  );
  const { availableTeams, teams, teamPaths, setTeamPaths } = useSelectedTeams();
  return (
    <NavPage>
      <ErrorBoundary>
        <View style={{ marginTop: 8, gap: 8, flex: 1 }}>
          <View style={{ flexDirection: "row", gap: 8 }}>
            <View style={{ gap: 4 }}>
              <FixtureLabel text={ph("day-ordinal") as string} />
              <DayPicker
                day={day}
                setDay={(d) => setDayString(canonicalDate(d))}
              />
            </View>
            {availableTeams?.length == 1 ? null : (
              <TeamsSelector
                availableTeams={availableTeams}
                selectedTeamPaths={teamPaths}
                setSelectedTeamPaths={setTeamPaths}
                single
              />
            )}
          </View>
          <View style={{ flex: 1, gap: 24 }}>
            {teams?.map((team, i) => (
              <CallSheet key={i} team={team} day={day} />
            ))}
          </View>
        </View>
      </ErrorBoundary>
    </NavPage>
  );
}
